import React, { useEffect, useState } from 'react';

interface LinkModel {
    url: string;
    name: string;
}

interface GeoPageContent {
    category: string;
    categoryCode: string;
    state: string;
    stateAbbreviation: string;
    city: string;
    metroId: number;
    categoryId: number;
    headerDescription: string;
    callOut: string;
    callOutDescription: string;
    categoryImageUrl: string;
    cityImageUrl: string;
}

interface GeoPageSchool {
    siteId: number;
    siteCode: string;
    name: string;
    url: string;
    price: number;
    address1: string;
    address2: string;
    address3: string;
    city: string;
    state: string;
    zipCode: string;
    numberOfStudents: number;
    programCount: number;
}

interface GeoPageProduct {
    productId: number;
    siteId: number;
    siteCode: string;
    websiteLocationTypeId: number;
    code: string;
    hours: number;
    title: string;
    description: string;
    seoName: string;
    accessTime: string;
    enrolling: string;
    price: number;
    productGroupType: string;
    totalSales: number;
    categoryName: string;
    subCategoryName: string;
    imageUrl: string;
    url: string;
    schools: GeoPageSchool[];
}

interface GeoPagePartnerSchool {
    partnerCodeOrSiteId: string;
    partnerName: string;
    url: string;
    address1: string;
    address2: string;
    address3: string;
    city: string;
    state: string;
    zipCode: string;
    products: GeoPageProduct[];
}

interface GeoPagePopularCategory {
    categoryName: string;
    code: string;
    totalSales: number;
    imageUrl: string;
    url: string;
}

interface GeoPageData {
    content: GeoPageContent;
    topSchools: GeoPagePartnerSchool[];
    topProducts: GeoPageProduct[];
    popularCategories: GeoPagePopularCategory[];
}

interface GeoPageProps {
    geoPage: GeoPageData;
    breadcrumbLinks: LinkModel[];
    pageUrl?: string;
}

const defaultCourseImageUrl = '/assets/image/default-course-thumbnail.jpg';
const popularTopicPageSize = 8;
const topSchoolPageSize = 4;
const mobileTopSchoolPageSize = 1;
const mobileCourseCarouselMediaQuery = '(max-width: 579px)';
const productSchoolPageSize = 3;

const styles = `
.geo-page {
    background: #fff;
    color: #333;
    font-family: "Open Sans", Verdana, Geneva, sans-serif;
    font-size: 14px;
    line-height: 1.45;
}

.geo-page a {
    color: #0079a8;
    text-decoration: none;
}

.geo-page button {
    font-family: inherit;
}

.geo-breadcrumb-bar {
    background: #f6f7f8;
    padding: 10px 20px;
}

.geo-breadcrumbs {
    font-size: 12px;
    margin: 0 auto;
    max-width: 1140px;
    padding: 0 24px;
}

.geo-breadcrumbs span + span::before {
    color: #000;
    content: ">";
    font-size: 10px;
    padding: 0 6px;
}

.geo-section,
.geo-container-preset {
    margin: 0 auto;
    max-width: 1140px;
    padding: 34px 24px;
}

.geo-title {
    background: #f6f7f8;
}

.geo-title-inner {
    align-items: center;
    display: flex;
    gap: 32px;
    justify-content: space-between;
    padding-bottom: 50px;
    padding-top: 60px;
}

.geo-title-copy {
    display: flex;
    flex-direction: column;
    max-width: 760px;
}

.geo-title .geo-title-copy h1 {
    color: #009ddc;
    font-family: "Open Sans", Verdana, Geneva, sans-serif !important;
    font-size: 2.57143rem;
    font-weight: 300;
    line-height: 1.2;
    margin: 0 0 20px;
}

.geo-title .geo-title-copy h1 strong {
    color: inherit;
    font-weight: 600;
}

.geo-title h1,
.geo-section h2,
.geo-container-preset h2 {
    color: #666;
    font-weight: 300;
    letter-spacing: 0;
    line-height: 1.2;
    margin: 0;
}

.geo-title h1 {
    color: #333;
    font-size: 32px;
}

.geo-section h2,
.geo-container-preset h2 {
    font-size: 2rem;
}

.geo-container-preset h2 {
    font-weight: 400;
}

.geo-title strong,
.geo-accent,
.e2g-ct-semi {
    font-weight: 600;
}

.geo-title strong {
    color: #2e7d32;
}

.geo-header-sm-icons {
    align-items: center;
    border-left: 1px solid #ccc;
    justify-content: center;
    line-height: 1.4rem;
    margin-left: auto;
}

.geo-tc-icons {
    line-height: 1.2rem;
    padding: 0 4px 0 14px;
    width: 44px;
}

.geo-tc-icons img {
    display: block;
    height: 44px;
    width: 44px;
}

.geo-tc-icons img:first-of-type {
    padding-bottom: 20px;
}

.geo-description {
    color: #666;
    display: block;
    font-size: 16px;
    margin-top: 12px;
}

.geo-callout {
    display: block;
    margin: 0 auto !important;
    width: 100%;
}

.geo-callout h2 {
    color: #052d3a !important;
    font-size: 1.8rem;
    font-weight: 400;
    line-height: 1.2;
    padding-bottom: 16px;
}

.geo-callout-copy p {
    color: #333;
    font-size: .85714rem;
    font-size: 16px;
    font-weight: 400;
    line-height: 1.9;
}

.geo-callout ol,
.geo-callout li {
    list-style: inherit !important;
    list-style-type: disc !important;
    max-width: 530px;
}

.geo-callout ul {
    clear: both;
    display: inline-block;
    font-size: 16px;
    line-height: 1.9;
    padding: 16px 0 16px 40px;
    width: auto;
}

.geo-hero-image {
    height: auto;
    float: right;
    margin: 0 0 30px 30px;
    max-width: 460px;
    object-fit: cover;
    width: 100%;
}

.h-flex,
.f-flex {
    display: flex;
}

.v-flex {
    display: flex;
    flex-direction: column;
}

.geo-btn-center,
.geo-load-more-row {
    display: flex;
    justify-content: center;
    width: 100%;
}

.geo-load-more-row {
    margin-top: 8px;
}

.geo-btn-green,
.geo-a-btn-blue {
    align-items: center;
    border: 0;
    border-radius: 4px;
    cursor: pointer;
    display: inline-flex;
    font-size: .9rem;
    font-weight: 600;
    justify-content: center;
    line-height: 1.15;
    padding: 8px 32px;
}

.geo-btn-green {
    background-color: #24b776;
    color: #fff;
}

.geo-a-btn-blue {
    background-color: #009ddc;
    color: #fff !important;
    text-align: center;
}

.geo-career-city-hdr,
.geo-top-sch-h2,
.geo-ctopic-hdr {
    border-bottom: 1px solid #666;
    color: #666;
    font-size: 2rem;
    line-height: 1.2;
    margin-bottom: 20px !important;
    padding-bottom: 14px;
    width: 100%;
}

.geo-career-city-hdr {
    border-bottom-color: #000;
}

.geo-top-sch-block {
    display: flex;
    flex-direction: row;
    justify-content: flex-start;
}

.geo-top-sch-inner {
    align-items: flex-start;
    display: flex;
    flex-direction: column;
    height: auto;
    margin-bottom: auto;
    width: 100%;
}

.etg-slider-geo-frame,
.geo-top-sch-frame {
    background: #f6f7f8;
    border: 1px solid #d9dbdc;
    border-radius: 6px;
    margin: 14px auto;
    max-width: 1140px;
    padding: 20px;
    position: relative;
    width: 100%;
}

.geo-sch-top-set,
.geo-local-sch {
    align-items: center;
    display: flex;
    flex-direction: row;
    width: 100%;
}

.geo-sch-top-set {
    background: #fff;
    border-radius: 4px;
    box-shadow: 0 1px 5px -3px rgba(0, 0, 0, .69);
    margin-bottom: 14px;
    padding: 10px;
}

.geo-img-set {
    align-self: stretch;
    border-left: 1px solid #ccc;
    display: block;
    flex: 0 0 1px;
    margin: 6px 18px 6px 0;
    min-height: 70px;
}

.geo-top-col-title {
    align-items: flex-start;
    flex: 1 1 auto;
    justify-content: center;
    line-height: 1.4rem;
    min-width: 0;
    padding-left: 14px;
}

.geo-top-col-l {
    margin-left: 0;
    margin-right: 18px;
}

.geo-top-h4 {
    color: #009ddc;
    font-size: 1.15rem;
    line-height: 1.2;
    margin: 0;
}

.geo-top-h4 span,
.geo-top-h4 span a {
    color: #004f6e;
    font-weight: 600;
}

.geo-top-sch-info-set {
    align-items: center;
    justify-content: center;
    line-height: 1.4rem;
    margin-left: 0;
    max-width: 390px;
}

.geo-map-pin-ico {
    align-items: center;
    display: flex;
    justify-content: center;
    padding: 0 12px 0 0;
}

.geo-map-pin-icon {
    background: #009ddc;
    border-radius: 50% 50% 50% 0;
    display: block;
    height: 18px;
    position: relative;
    transform: rotate(-45deg);
    width: 18px;
}

.geo-map-pin-icon::after {
    background: #fff;
    border-radius: 50%;
    content: "";
    height: 6px;
    left: 6px;
    position: absolute;
    top: 6px;
    width: 6px;
}

.geo-top-sch-addy {
    color: #666;
    font-size: .83rem;
}

.etg-slider {
    display: grid;
    gap: 12px;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    margin-top: 18px;
    overflow: visible;
}

.geo-top-sch-content-block {
    background: #fff;
    border-radius: 4px;
    display: flex;
    flex-direction: column;
    min-height: 420px;
    min-width: 0;
    padding: 12px;
    position: relative;
}

.geo-slider-shell {
    position: relative;
}

.geo-slider-arrow {
    background: transparent;
    border: none;
    color: transparent;
    cursor: pointer;
    display: block;
    font-size: 0;
    height: 34px;
    line-height: 0;
    outline: none;
    padding: 0;
    position: absolute;
    top: 50%;
    transform: translate(0, -50%);
    width: 34px;
    z-index: 999;
}

.geo-slider-arrow.slick-prev {
    background: url("/assets/svg/back.svg");
    background-size: contain;
    left: -38px;
}

.geo-slider-arrow.slick-next {
    background: url("/assets/svg/next.svg");
    background-size: contain;
    right: -38px;
}

.geo-slider-arrow.slick-prev:hover,
.geo-slider-arrow.slick-prev:focus {
    background: url("/assets/svg/back_disabled.svg");
    background-size: contain;
}

.geo-slider-arrow.slick-next:hover,
.geo-slider-arrow.slick-next:focus {
    background: url("/assets/svg/next_disabled.svg");
    background-size: contain;
}

.geo-slider-arrow.slick-prev.slick-disabled,
.geo-slider-arrow.slick-prev:disabled {
    background: url("/assets/svg/back_disabled.svg");
    background-size: contain;
    cursor: default;
}

.geo-slider-arrow.slick-next.slick-disabled,
.geo-slider-arrow.slick-next:disabled {
    background: url("/assets/svg/next_disabled.svg");
    background-size: contain;
    cursor: default;
}

.geo-top-sch-local,
.geo-pm-f-img {
    background: #f1f3f4 url("/assets/image/geo/ct-fallback.jpg") no-repeat center center;
    background-size: cover;
    overflow: hidden;
    position: relative;
}

.geo-top-sch-local {
    height: 122px;
    width: 100%;
}

.geo-top-sch-local img,
.geo-pm-f-img img {
    height: 100%;
    object-fit: cover;
    width: 100%;
}

.geo-top-sch-local::after,
.course-tops a::after {
    content: "";
    inset: 0;
    opacity: .42;
    position: absolute;
}

.geo-top-sch-ctitle {
    bottom: 0;
    color: #fff;
    display: inline-block;
    font-size: .74rem;
    font-weight: 600;
    left: 0;
    line-height: .95rem;
    padding: 4px 12px 8px;
    position: absolute;
    z-index: 1;
}

.geo-top-sch-ctitle.e2g-green {
    background-color: #24b776;
}

.geo-top-sch-ctitle.e2g-blue {
    background-color: #009ddc;
}

.geo-top-sch-well {
    color: #333;
    display: block;
    font-size: .9rem;
    font-weight: 600;
    height: 36px;
    line-height: 18px;
    margin: 8px 0;
    overflow: hidden;
}

.geo-top-sch-inf {
    color: #555;
    flex-direction: column;
    line-height: 1.2rem;
    width: 100%;
}

.geo-top-sch-set,
.geo-inf-two {
    display: block;
    font-size: .83rem;
}

.geo-top-sch-set i,
.geo-inf-two i,
.geo-tc-sec-ctr i {
    color: #009ddc;
    margin-right: 6px;
}

.geo-top-sch-subtxt {
    color: #666;
    display: block;
    font-size: .74rem;
    line-height: 1rem;
    margin: 8px 0 12px;
}

.button-set {
    align-items: stretch;
    display: flex;
    flex-direction: column;
    margin-top: auto;
    width: 100%;
}

.geo-top-sch-pinfo {
    background: #f1f3f4;
    border-radius: 4px;
    color: #24b776;
    display: block;
    font-size: 1rem;
    font-weight: 600;
    margin-bottom: 8px;
    padding: 8px 32px;
    text-align: center;
}

.geo-top-sch-block .geo-top-sch-frame {
    margin-bottom: 20px;
}

.geo-local-sch {
    background: #fff;
    border-radius: 4px;
    box-shadow: 0 1px 5px -3px rgba(0, 0, 0, .69);
    margin-bottom: 14px;
    padding: 10px;
}

.geo-pm-f-img {
    flex: 0 0 194px;
    height: 110px;
    margin-right: 4px;
    max-width: 194px;
    width: 100%;
}

.geo-top-col-title,
.geo-top-sch-title {
    flex: 1 1 auto;
    line-height: 1.4rem;
    margin-right: auto;
    min-width: 0;
    padding-left: 14px;
}

.geo-tc-secondary-inf {
    display: flex;
    flex-wrap: wrap;
    font-size: .76rem;
    gap: 0;
    line-height: 1.2rem;
    padding-top: 4px;
    width: 100%;
}

.geo-tc-secondary-inf .get-tc-sec-item {
    color: #009ddc;
    margin-bottom: 4px;
    margin-right: 14px;
}

.geo-tc-secondary-inf .get-tc-sec-item i {
    margin-right: 4px;
}

.geo-tc-secondary-inf .get-tc-sec-item span:not(:last-child) {
    margin-right: 4px;
}

.get-tc-sec-item {
    align-items: center;
    display: flex;
    font-size: .83rem;
}

.geo-tc-sec-ctr {
    align-items: center;
    display: flex;
    flex-direction: row;
    line-height: 1rem;
}

.get-tc-col,
.get-sch-col {
    align-items: center;
    display: flex;
    justify-content: flex-start;
}

.get-tc-col {
    color: #052d3a !important;
}

.geo-top-h4.get-tc-col span {
    color: #052d3a;
}

.get-sch-col {
    color: #009ddc !important;
}

.get-tc-link-alt {
    color: #333 !important;
    line-height: 1rem;
    text-decoration: underline;
}

.geo-stext-c {
    color: #666;
    display: block;
    font-size: 1.5rem;
    font-weight: 500;
    padding: 20px 0;
    text-align: center;
}

.geo-stext-c span {
    font-weight: 600;
}

.geo-product-school-list {
    display: grid;
    gap: 12px;
}

.geo-t-info-buy {
    align-items: center;
    display: flex;
    flex-direction: column;
    justify-content: center;
    line-height: 1.2rem;
    margin: 20px;
    min-width: 160px;
}

.geo-t-info-buy span {
    color: #24b776;
    display: block;
    font-size: 2rem;
    font-weight: 600;
    padding: 0 0 20px;
    text-align: center;
}

.get-sch-col {
    color: #666;
}

.get-tc-b {
    font-weight: 700;
    padding-left: 4px;
}

.geo-ctopic-frame {
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
}

.geo-topic-list {
    display: flex;
    flex-wrap: wrap;
    width: 100%;
}

.course-tops {
    align-items: center;
    border-radius: 4px;
    cursor: pointer;
    display: flex;
    flex-direction: column;
    justify-content: center;
    margin: 0 1% 1% 0;
    max-height: 144px;
    max-width: 24%;
    overflow: hidden;
    position: relative;
    text-align: center;
    width: 100%;
}

.course-tops a {
    display: block;
    height: 144px;
    position: relative;
    width: 100%;
}

.course-tops img {
    border-radius: 4px;
    height: 100%;
    object-fit: cover;
    width: 100%;
}

.geo-ct-span {
    color: #fff;
    display: block;
    font-size: 28px;
    left: 50%;
    line-height: 2.1rem;
    padding: 0 8px;
    position: absolute;
    text-align: center;
    top: 50%;
    transform: translate(-50%, -50%);
    width: 100%;
    z-index: 2;
}

@media (max-width: 1200px) {
    .geo-slider-arrow.slick-prev {
        left: -16px;
    }

    .geo-slider-arrow.slick-next {
        right: -16px;
    }
}

@media (min-width: 960px) {
    .etg-slider {
        grid-template-columns: repeat(4, minmax(0, 1fr));
    }
}

@media (max-width: 960px) {
    .etg-slider {
        grid-template-columns: repeat(3, minmax(0, 1fr));
    }

    .course-tops {
        max-width: 31%;
    }
}

@media (max-width: 768px) {
    .geo-section,
    .geo-container-preset {
        padding: 26px 20px;
    }

    .geo-title-inner,
    .geo-callout {
        display: block;
    }

    .geo-title-inner {
        padding-bottom: 38px;
        padding-top: 42px;
    }

    .geo-title h1 {
        font-size: 28px;
    }

    .geo-career-city-hdr,
    .geo-top-sch-h2,
    .geo-ctopic-hdr {
        text-align: center;
    }

    .geo-hero-image {
        float: none;
        margin: 0 auto 30px;
        max-width: 460px;
    }

    .geo-sch-top-set,
    .geo-local-sch {
        align-items: flex-start;
        flex-direction: column;
    }

    .geo-img-set {
        display: none;
    }

    .geo-top-sch-info-set {
        border-left: 0;
        margin-left: 0;
        max-width: none;
        padding-left: 0;
    }

    .geo-header-sm-icons {
        border-left: 0;
        border-top: 1px solid #ccc;
        margin-left: 0;
        margin-top: 20px;
        padding-top: 10px;
    }

    .geo-tc-icons {
        flex-direction: row;
        margin: 30px 0;
        padding: 0;
        width: auto;
    }

    .geo-tc-icons img {
        height: 30px;
        width: 30px;
    }

    .geo-tc-icons img:first-of-type {
        margin-right: 20px;
        padding: 0;
    }

    .geo-map-pin-ico {
        padding-left: 0;
    }

    .etg-slider {
        grid-template-columns: repeat(2, minmax(0, 1fr));
    }

    .geo-pm-f-img {
        flex-basis: auto;
        height: 230px;
        margin: 10px auto 16px;
        max-width: 360px;
    }

    .geo-t-info-buy {
        align-items: stretch;
        margin: 18px 0 0;
        min-width: 0;
        width: 100%;
    }

    .geo-t-info-buy span {
        padding: 0 0 12px;
    }

    .course-tops {
        max-width: 48%;
    }
}

@media (max-width: 579px) {
    .etg-slider {
        grid-template-columns: 100%;
    }

    .course-tops {
        margin-bottom: 20px;
        max-width: 100%;
    }

    .geo-top-sch-content-block {
        min-height: 0;
    }
}
`;

const buildAddress = (
    item: Pick<
        GeoPageSchool | GeoPagePartnerSchool,
        'address1' | 'address2' | 'address3' | 'city' | 'state' | 'zipCode'
    >,
) => {
    const street = [item.address1, item.address2, item.address3].map((value) => value?.trim()).filter(Boolean);
    const cityStateZip = [item.city, item.state, item.zipCode].filter(Boolean).join(', ').replace(', ,', ',');

    return [...street, cityStateZip].filter(Boolean).join(' ');
};

const formatMoney = (value: number) =>
    value > 0 ? value.toLocaleString('en-US', { style: 'currency', currency: 'USD' }) : '';

const formatHours = (hours: number) => (hours > 0 ? `${Math.round(hours)} Course Hrs` : '');

const stripDescriptionMarkup = (description: string) =>
    description
        .replace(/<[^>]*>/g, ' ')
        .replace(/&lt;[^&]*?&gt;/gi, ' ')
        .replace(/&nbsp;/gi, ' ')
        .replace(/&quot;/gi, '"')
        .replace(/&#39;/gi, "'")
        .replace(/&amp;/gi, '&')
        .replace(/\s+/g, ' ')
        .trim();

const renderCourseDescription = (description: string) => {
    const normalizedDescription = stripDescriptionMarkup(description);

    return normalizedDescription ? <span className="geo-top-sch-subtxt">{normalizedDescription}</span> : null;
};

const getCourseCarouselPageSize = () => {
    if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
        return topSchoolPageSize;
    }

    return window.matchMedia(mobileCourseCarouselMediaQuery).matches ? mobileTopSchoolPageSize : topSchoolPageSize;
};

const useCourseCarouselPageSize = () => {
    const [pageSize, setPageSize] = useState(getCourseCarouselPageSize);

    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
            return undefined;
        }

        const mediaQueryList = window.matchMedia(mobileCourseCarouselMediaQuery);
        const updatePageSize = () => setPageSize(mediaQueryList.matches ? mobileTopSchoolPageSize : topSchoolPageSize);

        updatePageSize();

        if (typeof mediaQueryList.addEventListener === 'function') {
            mediaQueryList.addEventListener('change', updatePageSize);
            return () => mediaQueryList.removeEventListener('change', updatePageSize);
        }

        mediaQueryList.addListener(updatePageSize);
        return () => mediaQueryList.removeListener(updatePageSize);
    }, []);

    return pageSize;
};

const buildSharePageUrl = (pageUrl = '') => {
    if (pageUrl.trim().length > 0) {
        return pageUrl;
    }

    if (typeof window !== 'undefined' && window.location?.href) {
        return window.location.href;
    }

    return '';
};

const renderShareLinks = (content: GeoPageContent, pageUrl?: string) => {
    const sharePageUrl = buildSharePageUrl(pageUrl);
    const shareTitle = `${content.category || 'Online'} Courses in ${content.city}`;
    const facebookUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(sharePageUrl)}`;
    const twitterUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(
        sharePageUrl,
    )}&text=${encodeURIComponent(shareTitle)}`;

    return (
        <div className="h-flex geo-header-sm-icons">
            <div className="v-flex geo-tc-icons">
                <a href={facebookUrl} target="_blank" rel="noopener noreferrer">
                    <img src="/assets/svg/fbook_ico.svg" alt="Share with Facebook" />
                </a>
                <a href={twitterUrl} target="_blank" rel="noopener noreferrer">
                    <img src="/assets/svg/twitter_ico.svg" alt="Share with Twitter" />
                </a>
            </div>
        </div>
    );
};

const renderBreadcrumb = (breadcrumbLinks: LinkModel[]) => (
    <div className="geo-breadcrumb-bar">
        <nav className="geo-breadcrumbs" aria-label="Breadcrumb">
            {breadcrumbLinks.map((link, index) => (
                <span key={`${link.name}-${index}`}>{link.url ? <a href={link.url}>{link.name}</a> : link.name}</span>
            ))}
        </nav>
    </div>
);

const buildCourseTypeClassName = (productGroupType: string): string => {
    const normalizedProductGroupType = productGroupType.trim().toLowerCase();
    const colorClassName = normalizedProductGroupType.includes('advanced career training')
        ? 'e2g-green'
        : normalizedProductGroupType.includes('fundamentals')
        ? 'e2g-blue'
        : '';

    return ['geo-top-sch-ctitle', colorClassName].filter(Boolean).join(' ');
};

const renderCourseTile = (product: GeoPageProduct) => (
    <article className="v-flex geo-top-sch-content-block" key={`${product.productId}-${product.siteId}`}>
        <div className="geo-top-sch-local">
            {product.productGroupType && (
                <span className={buildCourseTypeClassName(product.productGroupType)}>{product.productGroupType}</span>
            )}
            <img src={product.imageUrl || defaultCourseImageUrl} alt="" />
        </div>
        <span className="geo-top-sch-well" title={product.title}>
            {product.title}
        </span>
        <div className="f-flex geo-top-sch-inf">
            <span className="geo-top-sch-set">
                <i className="far fa-clock" aria-hidden="true" />
                {[product.accessTime, formatHours(product.hours)].filter(Boolean).join(' / ')}
            </span>
            {product.enrolling && (
                <span className="geo-inf-two">
                    <i className="far fa-clipboard" aria-hidden="true" />
                    {product.enrolling}
                </span>
            )}
        </div>
        {product.description && renderCourseDescription(product.description)}
        <div className="button-set v-flex">
            {formatMoney(product.price) && (
                <span className="price geo-top-sch-pinfo">{formatMoney(product.price)}</span>
            )}
            {product.url && (
                <a className="geo-a-btn-blue" href={product.url}>
                    View Course
                </a>
            )}
        </div>
    </article>
);

const CourseCarousel: React.FC<{ products: GeoPageProduct[] }> = ({ products }) => {
    const [startIndex, setStartIndex] = useState(0);
    const pageSize = useCourseCarouselPageSize();
    const maxStartIndex = Math.max(0, products.length - pageSize);
    const normalizedStartIndex = Math.min(startIndex, maxStartIndex);
    const visibleProducts = products.slice(normalizedStartIndex, normalizedStartIndex + pageSize);
    const showArrows = products.length > pageSize;

    useEffect(() => {
        if (startIndex > maxStartIndex) {
            setStartIndex(maxStartIndex);
        }
    }, [maxStartIndex, startIndex]);

    if (!products.length) {
        return null;
    }

    return (
        <div className="geo-slider-shell">
            {showArrows && (
                <button
                    aria-label="Show previous courses"
                    className={`geo-slider-arrow geo-slider-arrow-prev slick-prev slick-arrow${
                        normalizedStartIndex === 0 ? ' slick-disabled' : ''
                    }`}
                    disabled={normalizedStartIndex === 0}
                    type="button"
                    onClick={() => setStartIndex(Math.max(0, normalizedStartIndex - pageSize))}
                >
                    Previous
                </button>
            )}
            <div className="etg-slider slick-initialized slick-slider">{visibleProducts.map(renderCourseTile)}</div>
            {showArrows && (
                <button
                    aria-label="Show next courses"
                    className={`geo-slider-arrow geo-slider-arrow-next slick-next slick-arrow${
                        normalizedStartIndex === maxStartIndex ? ' slick-disabled' : ''
                    }`}
                    disabled={normalizedStartIndex === maxStartIndex}
                    type="button"
                    onClick={() => setStartIndex(Math.min(maxStartIndex, normalizedStartIndex + pageSize))}
                >
                    Next
                </button>
            )}
        </div>
    );
};

const TopSchoolsByLocation: React.FC<{ content: GeoPageContent; schools: GeoPagePartnerSchool[] }> = ({
    content,
    schools,
}) => {
    const [visibleSchoolCount, setVisibleSchoolCount] = useState(topSchoolPageSize);
    const visibleSchools = schools.slice(0, visibleSchoolCount);

    if (!schools.length) {
        return null;
    }

    return (
        <section className="geo-container-preset">
            <h2 className="geo-career-city-hdr">
                Top Schools in <span className="e2g-ct-semi">{content.city}</span>
            </h2>
            {visibleSchools.map((school) => (
                <article className="etg-slider-geo-frame" key={school.partnerCodeOrSiteId || school.partnerName}>
                    <div className="h-flex geo-sch-top-set">
                        <div className="v-flex geo-top-col-title geo-top-col-l">
                            <h4 className="geo-top-h4">
                                <span>
                                    {school.url ? <a href={school.url}>{school.partnerName}</a> : school.partnerName}
                                </span>
                            </h4>
                        </div>
                        {buildAddress(school) && (
                            <>
                                <div className="geo-img-set" aria-hidden="true" />
                                <div className="h-flex geo-top-sch-info-set">
                                    <div className="geo-map-pin-ico">
                                        <span className="geo-map-pin-icon" aria-hidden="true" />
                                    </div>
                                    <div className="v-flex">
                                        <span className="geo-top-sch-addy">{buildAddress(school)}</span>
                                    </div>
                                </div>
                            </>
                        )}
                    </div>
                    <CourseCarousel products={school.products} />
                </article>
            ))}
            {visibleSchoolCount < schools.length && (
                <div className="geo-btn-center">
                    <button
                        className="geo-btn-green"
                        type="button"
                        onClick={() => setVisibleSchoolCount(visibleSchoolCount + topSchoolPageSize)}
                    >
                        Load More Schools
                    </button>
                </div>
            )}
        </section>
    );
};

const CategoryProductSchools: React.FC<{ content: GeoPageContent; product: GeoPageProduct }> = ({
    content,
    product,
}) => {
    const [visibleSchoolCount, setVisibleSchoolCount] = useState(productSchoolPageSize);
    const visibleSchools = product.schools.slice(0, visibleSchoolCount);

    return (
        <article className="topschool-module geo-top-sch-frame">
            <div>
                <div className="h-flex geo-local-sch">
                    <div className="geo-pm-f-img">
                        <img src={product.imageUrl || defaultCourseImageUrl} alt="" />
                    </div>
                    <div className="v-flex geo-top-sch-title">
                        <h4 className="geo-top-h4 get-tc-col">
                            <span>{product.title}</span>
                        </h4>
                        {product.description && renderCourseDescription(product.description)}
                        <div className="h-flex geo-tc-secondary-inf">
                            <div className="get-tc-sec-item geo-tc-sec-ctr">
                                <i className="far fa-clock" aria-hidden="true" />
                                <span className="get-tc-col">
                                    {[product.accessTime, formatHours(product.hours)].filter(Boolean).join(' / ')}
                                </span>
                            </div>
                            {product.enrolling && (
                                <div className="get-tc-sec-item geo-tc-sec-ctr">
                                    <i className="far fa-clipboard" aria-hidden="true" />
                                    <span className="get-tc-col">{product.enrolling}</span>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
                <span className="e2g-cat-subtext geo-stext-c">
                    Available from these <span>{content.city}</span> Colleges
                </span>
                <div className="geo-product-school-list">
                    {visibleSchools.map((school) => (
                        <div className="h-flex geo-sch-top-set" key={`${product.productId}-${school.siteId}`}>
                            <div className="v-flex geo-top-col-title">
                                <h4 className="geo-top-h4 get-tc-col">
                                    <span>{school.name}</span>
                                </h4>
                                {buildAddress(school) && (
                                    <div className="h-flex geo-tc-secondary-inf">
                                        <div className="get-tc-sec-item get-sch-col">
                                            <i className="fas fa-map-marker-alt" aria-hidden="true" />
                                            <span className="get-tc-link-alt">{buildAddress(school)}</span>
                                        </div>
                                    </div>
                                )}
                                {(school.numberOfStudents > 0 || school.programCount > 0) && (
                                    <div className="h-flex geo-tc-secondary-inf">
                                        {school.numberOfStudents > 0 && (
                                            <div className="get-tc-sec-item get-tc-col">
                                                <span>Number of Students</span>
                                                <span className="get-tc-b">{school.numberOfStudents}</span>
                                            </div>
                                        )}
                                        {school.programCount > 0 && (
                                            <div className="get-tc-sec-item get-tc-col">
                                                <span>Courses offered</span>
                                                <span className="get-tc-b">{school.programCount}</span>
                                            </div>
                                        )}
                                    </div>
                                )}
                            </div>
                            <div className="h-flex geo-top-sch-info-set">
                                <div className="v-flex geo-t-info-buy">
                                    {formatMoney(school.price) && <span>{formatMoney(school.price)}</span>}
                                    {school.url && (
                                        <a className="geo-a-btn-blue" href={school.url}>
                                            Learn More
                                        </a>
                                    )}
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
                {visibleSchoolCount < product.schools.length && (
                    <div className="geo-btn-center">
                        <button
                            className="geo-btn-green"
                            type="button"
                            onClick={() => setVisibleSchoolCount(visibleSchoolCount + productSchoolPageSize)}
                        >
                            Load More Schools
                        </button>
                    </div>
                )}
            </div>
        </article>
    );
};

const TopSchoolsByCategory: React.FC<{ content: GeoPageContent; products: GeoPageProduct[] }> = ({
    content,
    products,
}) => {
    if (!products.length) {
        return null;
    }

    return (
        <section className="geo-container-preset geo-top-sch-block">
            <div className="geo-top-sch-inner">
                <h2 className="geo-top-sch-h2">
                    Top Schools in <span className="e2g-ct-semi">{content.city}</span>
                </h2>
                {products.map((product) => (
                    <CategoryProductSchools content={content} product={product} key={product.productId} />
                ))}
            </div>
        </section>
    );
};

const PopularCategories: React.FC<{ content: GeoPageContent; categories: GeoPagePopularCategory[] }> = ({
    content,
    categories,
}) => {
    const [visibleCategoryCount, setVisibleCategoryCount] = useState(popularTopicPageSize);
    const visibleCategories = categories.slice(0, visibleCategoryCount);

    if (!categories.length) {
        return null;
    }

    return (
        <section className="geo-container-preset geo-ctopic-frame">
            <h2 className="geo-ctopic-hdr">
                Popular Course Topics in <span className="e2g-ct-semi">{content.city}</span>
            </h2>
            <div className="geo-topic-list">
                {visibleCategories.map((category) => (
                    <div className="course-tops img-overlay-frame" key={`${category.code}-${category.categoryName}`}>
                        <a href={category.url}>
                            <span className="geo-ct-span">{category.categoryName}</span>
                            <img
                                src={category.imageUrl || defaultCourseImageUrl}
                                alt=""
                                className="geo-img-right no-margin"
                            />
                        </a>
                    </div>
                ))}
            </div>
            {visibleCategoryCount < categories.length && (
                <div className="geo-load-more-row">
                    <button
                        className="geo-btn-green"
                        type="button"
                        onClick={() => setVisibleCategoryCount(visibleCategoryCount + popularTopicPageSize)}
                    >
                        Load More Course Topics
                    </button>
                </div>
            )}
        </section>
    );
};

const GeoPage: React.FC<GeoPageProps> = ({ geoPage, breadcrumbLinks, pageUrl }) => {
    const { content, topSchools, topProducts, popularCategories } = geoPage;
    const categoryName = content.category || 'Online';
    const heroImage = content.category ? content.categoryImageUrl : content.cityImageUrl;

    return (
        <main className="geo-page">
            <style>{styles}</style>
            {renderBreadcrumb(breadcrumbLinks)}
            <section className="geo-title">
                <div className="geo-section geo-title-inner">
                    <div className="geo-title-copy">
                        <h1>
                            {categoryName} Courses in{' '}
                            <strong>
                                {content.city}, {content.stateAbbreviation}
                            </strong>
                        </h1>
                        {content.headerDescription && (
                            <span className="geo-description">{content.headerDescription}</span>
                        )}
                    </div>
                    {renderShareLinks(content, pageUrl)}
                </div>
            </section>
            <section className="geo-section geo-callout">
                <img
                    className="geo-hero-image"
                    src={heroImage || defaultCourseImageUrl}
                    alt={`${categoryName} Courses in ${content.city}, ${content.stateAbbreviation}`}
                />
                <div className="geo-callout-copy">
                    <h2>{content.callOut}</h2>
                    {content.callOutDescription && (
                        <div dangerouslySetInnerHTML={{ __html: content.callOutDescription }} />
                    )}
                </div>
            </section>
            {content.category ? (
                <TopSchoolsByCategory content={content} products={topProducts} />
            ) : (
                <TopSchoolsByLocation content={content} schools={topSchools} />
            )}
            <PopularCategories content={content} categories={popularCategories} />
        </main>
    );
};

export { GeoPage, GeoPageProps };
